import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@ah/design';
import {
  Share,
  PlusSquare,
  Home,
  MoreVertical,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import {
  detectPlatform,
  dismiss,
  type BeforeInstallPromptEvent,
  type Platform,
} from '../install';
import { MAP_BG_PNG } from '../assets';

function MapBackground() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none select-none absolute inset-x-0 top-0 h-[65%]"
      style={{
        backgroundImage: `url(${MAP_BG_PNG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
      }}
    />
  );
}

function Step({
  index,
  icon,
  children,
}: {
  index: number;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
        {index}
      </div>
      <div className="flex-1 pt-1.5 text-sm text-foreground/90 leading-relaxed">
        {children}
      </div>
      <div className="shrink-0 pt-1.5 text-muted-foreground">{icon}</div>
    </li>
  );
}

function IosSafariSteps() {
  return (
    <ol className="space-y-4 text-left">
      <Step index={1} icon={<Share size={20} />}>
        Tap the <strong>Share</strong> button in Safari
      </Step>
      <Step index={2} icon={<PlusSquare size={20} />}>
        Scroll and tap <strong>Add to Home Screen</strong>
      </Step>
      <Step index={3} icon={<Home size={20} />}>
        Open <strong>Underlings</strong> from your home screen
      </Step>
    </ol>
  );
}

function AndroidChromeSteps({ onInstall, canPrompt }: { onInstall: () => void; canPrompt: boolean }) {
  if (canPrompt) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          One tap to add Underlings to your home screen.
        </p>
        <Button onClick={onInstall} className="w-full" size="lg">
          Install app
        </Button>
      </div>
    );
  }
  return (
    <ol className="space-y-4 text-left">
      <Step index={1} icon={<MoreVertical size={20} />}>
        Open the Chrome menu (top-right)
      </Step>
      <Step index={2} icon={<PlusSquare size={20} />}>
        Tap <strong>Install app</strong> or <strong>Add to Home Screen</strong>
      </Step>
      <Step index={3} icon={<Home size={20} />}>
        Open <strong>Underlings</strong> from your home screen
      </Step>
    </ol>
  );
}

function AndroidOtherSteps() {
  return (
    <ol className="space-y-4 text-left">
      <Step index={1} icon={<MoreVertical size={20} />}>
        Open your browser menu
      </Step>
      <Step index={2} icon={<PlusSquare size={20} />}>
        Tap <strong>Add to Home Screen</strong> (or <strong>Install</strong>)
      </Step>
      <Step index={3} icon={<Home size={20} />}>
        Open <strong>Underlings</strong> from your home screen
      </Step>
    </ol>
  );
}

// Canonical install URL — always point new installs at mobile.underlings.com
// even when this page is served from the legacy m. origin.
const INSTALL_URL = 'https://mobile.underlings.com';

function DesktopQrCode() {
  return (
    <div className="space-y-5 flex flex-col items-center">
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <QRCodeSVG value={INSTALL_URL} size={176} level="M" />
      </div>
      <p className="text-sm text-muted-foreground">
        Scan with your phone camera, or open{' '}
        <span className="font-medium text-foreground">mobile.underlings.com</span> on mobile.
      </p>
    </div>
  );
}

function IosOtherInstructions() {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — fall through, user will copy from address bar
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        On iOS, apps can only be installed from <strong>Safari</strong>. Please open
        this page in Safari to continue.
      </p>
      <Button onClick={copy} variant="outline" className="w-full" size="lg">
        {copied ? (
          <>
            <Check size={18} className="mr-2" /> Link copied
          </>
        ) : (
          <>
            <Copy size={18} className="mr-2" /> Copy link to open in Safari
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground text-left">
        Then: open Safari, paste the link, and follow the install prompt.
      </p>
    </div>
  );
}

export function Install({ onContinue }: { onContinue: () => void }) {
  const [platform, setPlatform] = useState<Platform>('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    setPlatform(detectPlatform());

    function handler(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function triggerInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') {
      // Browser will reload us in standalone mode; nothing else to do.
    }
  }

  function continueInBrowser() {
    dismiss();
    onContinue();
  }

  const headline =
    platform === 'desktop'
      ? 'Open on your phone'
      : platform === 'ios-other'
      ? 'Open in Safari to install'
      : 'Install Underlings';

  const subhead =
    platform === 'desktop'
      ? 'Underlings is built for your phone. Scan the QR code to continue.'
      : platform === 'ios-other'
      ? 'iOS only allows installs from Safari.'
      : 'Get the full experience — a tap away from your home screen.';

  return (
    <div className="relative min-h-dvh grid place-items-center bg-gradient-to-b from-[hsl(34,45%,96%)] to-background px-6 overflow-hidden">
      <MapBackground />
      <div className="relative max-w-sm w-full text-center space-y-8 py-12">
        <div className="space-y-3">
          <img
            src="/pwa-192.png"
            alt=""
            className="mx-auto h-20 w-20 rounded-2xl shadow-sm"
          />
          <h1 className="text-3xl font-bold tracking-tight">{headline}</h1>
          <p className="text-sm text-muted-foreground">{subhead}</p>
        </div>

        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          {platform === 'ios-safari' && <IosSafariSteps />}
          {platform === 'ios-other' && <IosOtherInstructions />}
          {platform === 'android-chrome' && (
            <AndroidChromeSteps onInstall={triggerInstall} canPrompt={!!deferredPrompt} />
          )}
          {platform === 'android-other' && <AndroidOtherSteps />}
          {platform === 'desktop' && <DesktopQrCode />}
          {platform === 'unknown' && (
            <p className="text-sm text-muted-foreground">
              You can continue in your browser, or open this page on your phone to install the app.
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={continueInBrowser}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Continue in browser <ExternalLink size={14} />
        </button>
      </div>
    </div>
  );
}
